import { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import {
  MBR_PERIOD_OLDER_VALUE,
  MBR_PERIOD_REGEX,
  buildMbrPeriodOptions,
} from '../mbrPeriodOptions';

interface MbrPeriodPickerProps {
  /** Canonical YYYY-MM (or '' when the entry has none). */
  value: string;
  /** Injected for deterministic tests; defaults to a real Date at render-time. */
  now?: Date;
  /** Fired with the canonical YYYY-MM whenever the user produces a valid value. */
  onChange: (value: string) => void;
  /**
   * Fired whenever validity flips. Save-buttons in the parent should disable
   * while this is `false` to mirror the slack-bot picker's "you can't submit
   * a malformed period" guarantee.
   */
  onValidityChange?: (valid: boolean) => void;
}

/**
 * AOZONE-59: mbrPeriod picker for the admin-ui edit surface. Mirrors the
 * slack-bot step-1 modal UX: a 6-month rolling dropdown plus an "Older…"
 * sentinel that reveals a free-text YYYY-MM input. The input is validated
 * client-side against `MBR_PERIOD_REGEX`; the server re-validates on PATCH so
 * a hand-crafted request cannot smuggle in a malformed period.
 *
 * When the entry's current `value` is outside the 6-month window (e.g. an
 * admin opening a 2024 entry today), the picker auto-selects "Older…" and
 * seeds the input with that value so the admin sees what's there without
 * having to retype it.
 */
export function MbrPeriodPicker({
  value,
  now,
  onChange,
  onValidityChange,
}: MbrPeriodPickerProps) {
  const options = useMemo(() => buildMbrPeriodOptions(now ?? new Date()), [now]);
  const inDropdown = options.some(
    (o) => o.value !== MBR_PERIOD_OLDER_VALUE && o.value === value,
  );

  const [showOlder, setShowOlder] = useState(!inDropdown && value !== '');
  const [olderInput, setOlderInput] = useState(!inDropdown ? value : '');

  // If the parent swaps in a new entry whose value falls outside the window,
  // re-seed the Older input. (Same-window changes flow through the <select/>.)
  useEffect(() => {
    if (!inDropdown && value !== '') {
      setShowOlder(true);
      setOlderInput(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const selectValue = showOlder ? MBR_PERIOD_OLDER_VALUE : value;
  const olderValid = MBR_PERIOD_REGEX.test(olderInput);
  const isValid = showOlder ? olderValid : MBR_PERIOD_REGEX.test(value);

  useEffect(() => {
    onValidityChange?.(isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  const onSelectChange = (next: string) => {
    if (next === MBR_PERIOD_OLDER_VALUE) {
      setShowOlder(true);
      // If the older-input already holds a valid value, surface it; otherwise
      // wait for the admin to type one in.
      if (MBR_PERIOD_REGEX.test(olderInput)) onChange(olderInput);
      return;
    }
    setShowOlder(false);
    onChange(next);
  };

  const onOlderInputChange = (next: string) => {
    setOlderInput(next);
    if (MBR_PERIOD_REGEX.test(next)) onChange(next);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        aria-label="MBR period"
        value={selectValue}
        onChange={(e) => onSelectChange(e.target.value)}
        className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {showOlder && (
        <div className="flex flex-col gap-1">
          <Input
            aria-label="Older period (YYYY-MM)"
            aria-invalid={olderInput !== '' && !olderValid ? 'true' : undefined}
            placeholder="YYYY-MM"
            value={olderInput}
            onChange={(e) => onOlderInputChange(e.target.value)}
            className="w-32"
          />
          {olderInput !== '' && !olderValid && (
            <span className="text-xs font-medium text-red-600">
              Use YYYY-MM (e.g. 2025-08)
            </span>
          )}
        </div>
      )}
    </div>
  );
}
